import type { AssignmentSource } from './taskAssignmentForm';

export type InactiveAssigneeKind = 'employee' | 'position' | 'schedule';

export type InactiveAssignee = {
  kind: InactiveAssigneeKind;
  id: number;
  name: string;
};

export type AssignmentIdGroups = {
  employeeIds: number[];
  positionIds: number[];
  scheduleIds: number[];
};

export type InactiveResolution = 'remove' | 'ignore' | 'cancel';

export function buildAssignmentNameMaps(source?: AssignmentSource | null) {
  const employees = new Map<number, string>();
  const positions = new Map<number, string>();
  const schedules = new Map<number, string>();

  for (const e of source?.assignedTo ?? []) {
    const id = Number(e.id);
    if (!Number.isFinite(id)) continue;
    const name = `${e.firstName ?? ''} ${e.lastName ?? ''}`.trim();
    employees.set(id, name || e.email || `#${id}`);
  }
  for (const p of source?.assignedToPositions ?? []) {
    const id = Number(p.id);
    if (!Number.isFinite(id)) continue;
    positions.set(id, (p.title || p.name || '').trim() || `#${id}`);
  }
  for (const s of source?.assignedToSchedules ?? []) {
    const id = Number(s.id);
    if (!Number.isFinite(id)) continue;
    schedules.set(id, (s.name ?? '').trim() || `#${id}`);
  }

  return { employees, positions, schedules };
}

export function detectInactiveAssignees(
  form: AssignmentIdGroups,
  active: AssignmentIdGroups,
  names: ReturnType<typeof buildAssignmentNameMaps>
): InactiveAssignee[] {
  const activeEmp = new Set(active.employeeIds.map(Number));
  const activePos = new Set(active.positionIds.map(Number));
  const activeSched = new Set(active.scheduleIds.map(Number));
  const out: InactiveAssignee[] = [];
  const seen = new Set<string>();

  const push = (kind: InactiveAssigneeKind, rawId: number) => {
    const id = Number(rawId);
    if (!Number.isFinite(id)) return;
    const key = `${kind}:${id}`;
    if (seen.has(key)) return;
    const activeSet = kind === 'employee' ? activeEmp : kind === 'position' ? activePos : activeSched;
    if (activeSet.has(id)) return;
    seen.add(key);
    const nameMap =
      kind === 'employee' ? names.employees : kind === 'position' ? names.positions : names.schedules;
    out.push({ kind, id, name: nameMap.get(id) ?? `#${id}` });
  };

  form.employeeIds.forEach(id => push('employee', id));
  form.positionIds.forEach(id => push('position', id));
  form.scheduleIds.forEach(id => push('schedule', id));
  return out;
}

export function stripInactiveAssignees(
  form: AssignmentIdGroups,
  inactive: InactiveAssignee[]
): AssignmentIdGroups {
  const emp = new Set(inactive.filter(i => i.kind === 'employee').map(i => i.id));
  const pos = new Set(inactive.filter(i => i.kind === 'position').map(i => i.id));
  const sched = new Set(inactive.filter(i => i.kind === 'schedule').map(i => i.id));
  return {
    employeeIds: form.employeeIds.filter(id => !emp.has(Number(id))),
    positionIds: form.positionIds.filter(id => !pos.has(Number(id))),
    scheduleIds: form.scheduleIds.filter(id => !sched.has(Number(id))),
  };
}

export function formatInactiveAssigneesMessage(inactive: InactiveAssignee[]): string {
  const lines = inactive.map(i => {
    const label = i.kind === 'employee' ? 'Employee' : i.kind === 'position' ? 'Position' : 'Schedule';
    return `${label}: ${i.name}`;
  });
  return lines.join(', ');
}

export function hasAnyAssignmentIds(groups: AssignmentIdGroups): boolean {
  return (
    groups.employeeIds.length > 0 ||
    groups.positionIds.length > 0 ||
    groups.scheduleIds.length > 0
  );
}
